import React, { Component } from 'react';
import { func, object, string } from 'prop-types';
import { append, contains, map } from 'ramda';
import Select from 'react-select';
import moment from 'moment';

// Redux
import { connect } from 'react-redux';
import {
  filter,
  getDisplayProperties,
  request,
} from '../../../../actions/waitlist/waitlistActions';
import * as WaitlistTypes from '../../../../actions/waitlist/types';

import LoadingState from '../../../LoadingState';
import DatePickerInput from '../../../DatePickerInput/DatePickerInput';
import TimeInput from '../../../TimeInput/TimeInput';
import Checkbox from '../../../Checkbox/Checkbox';

import cx from 'classnames';
import s from './Waitlist.css';


export class Waitlist extends Component {
  static propTypes = {
    closeAlertDialog: func,
    dateFormat: string,
    filter: func,
    getDisplayProperties: func,
    loading: object,
    openSuccessDialog: func,
    openErrorDialog: func,
    waitlist: object,
  };

  static defaultProps = {
    dateFormat: 'YYYY-MM-DD',
  };

  static dataInitialState = {
    filters: [],
    other: '',
    firstAvailable: true,
    date: null,
    when: 'between',
    startTime: -1,
    endTime: -1,
    notes: '',
  };

  constructor(props) {
    super(props);

    this.header = 'Waitlist';
    this.instructions = 'Send a request to our waitlist, and we will contact you with your appointment information.';
    this.whenOptions = [
      { value: 'between', label: 'between' },
      { value: 'before', label: 'before' },
      { value: 'after', label: 'after' },
    ];

    this.state = {
      data: Waitlist.dataInitialState,
      filterLabels: [],
      filterOptions: [],
      minDate: moment().format(props.dateFormat),
    };
  }

  componentWillMount = () => this.getDisplayProperties();

  getDisplayProperties = () => {
    this.props.getDisplayProperties();
    this.filter(0, this.state.data.filters);
  };

  filter = (index, items) => {

    const successCallback = () =>
      this.setState({
        filterOptions: append(this.filterItemsToSelectObj(this.props.waitlist.items), this.state.filterOptions),
      });

    this.props.filter({ id: index, items }, successCallback);

  };

  buildRequestObject = data => {

    // Default starting date time and ending date time to null, in the event first available was selected
    let startDttm = null;
    let endDttm = null;

    const firstAvailable = data.firstAvailable;
    if (!firstAvailable) {

      // No start time was chosen, therefore, default to 0 minutes since midnight
      let newStartTime = data.startTime > 0 ? data.startTime : 0;

      // No end time was chosen, therefore, default to the last minute of the day
      let newEndTime = data.endTime > 0 ? data.endTime : 1439;

      // Date format required by wait list request validator
      const dttmFormat = 'YYYY-MM-DD HH:mm:ss';

      // Convert the chosen date and times into a unix timestamp in milliseconds then format to meet the requests required format
      startDttm = moment(data.date).hour(newStartTime / 60).minute(newStartTime % 60).format(dttmFormat);
      endDttm = moment(data.date).hour(newEndTime / 60).minute(newEndTime % 60).format(dttmFormat);
    }

    let requestObject = {
      other: '',
      firstAvailable: firstAvailable,
      startDttm: startDttm,
      endDttm: endDttm,
      notes: data.notes,
      item1: {},
      item2: {},
      item3: {},
    };

    // Ensure that if the "filter input" text area is static and never toggles to build the request with the input value always
    const isToggleable = this.isOptionalToggleable();
    if (!isToggleable) {
      requestObject.other = data.other;
    }

    // Build each filter's selection values for the request object to consume
    data.filters.forEach((value, index) => {

      /*
        If we have a toggleable "filter input" area, ensure that a selection of "other" was made
        in addition so we know to build the request with that data
       */
      if (isToggleable && this.checkForSelectionOfOther(value)) {
        requestObject.other = data.other;
      }

      // Populate each item# field within our request object
      return requestObject[`item${index+1}`] =  value;

    });

    return requestObject;

  };

  clearData = () => this.setState({ data: Waitlist.dataInitialState });

  // If total visible filters displayed to the user have ALL been selected by the user, enable Request Waitlist button
  isValidRequest = () => {

    const visibleFilters = this.getVisibleFilterProperties(this.props.waitlist.filters);
    return visibleFilters.length === this.state.data.filters.length;

  };

  filterItemsToSelectObj = items => map(item => ({ value: JSON.stringify(item), label: item.label }) , items);

  changeDataValue = (field, value) => this.setState({ data: { ...this.state.data, [field]: value } });

  handleFilterSelection = (index, item) => {

    const filterOptions = this.state.filterOptions.slice(0, index + 1);
    const strategizedValues = append(JSON.parse(item.value), this.state.data.filters.slice(0, index));

    this.setState({
      data: {
        ...this.state.data,
        filters: strategizedValues
      },
      filterOptions,
    });

    this.filter(index + 1, strategizedValues);

  };

  handleDateChange = value => this.changeDataValue('date', value);

  handleOtherChange = event => this.changeDataValue('other', event.target.value);

  handleNotesChange = event => this.changeDataValue('notes', event.target.value);

  handleFirstAvailableSelection = value => {
    this.setState({
      data: {
        ...this.state.data,
        date: Waitlist.dataInitialState.date,
        startTime: Waitlist.dataInitialState.startTime,
        endTime: Waitlist.dataInitialState.endTime,
        when: Waitlist.dataInitialState.when,
        firstAvailable: value,
      }
    });
  };

  handleWhenSelection = value => {
    this.setState({
      data: {
        ...this.state.data,
        startTime: Waitlist.dataInitialState.startTime,
        endTime: Waitlist.dataInitialState.endTime,
        when: value.value,
      }
    });
  };

  handleStartTimeChange = value => this.changeDataValue('startTime', value);

  handleEndTimeChange = value => this.changeDataValue('endTime', value);

  handleSubmit = () => {
    const dataInitialStateStartTime = Waitlist.dataInitialState.startTime; // -1 - set in Constructor
    const dataInitialStateEndTime = Waitlist.dataInitialState.endTime; // -1 - set in Constructor
    const stateStartTime = this.state.data.startTime;
    const stateEndTime = this.state.data.endTime;
    const isNotDefaultTimes = stateStartTime !== dataInitialStateStartTime && stateEndTime !== dataInitialStateEndTime;

    // Validate if the first available selection was not made, then a date value was provided
    if (!this.isValidDate()) {

      this.props.openErrorDialog({
        title: 'Missing Date',
        content: 'Please enter the date on which you wish to wait list for.',
        okCallback: this.props.closeAlertDialog
      });

      // Short circuit
      return;

    }

    // Validate that a startTime cannot be greater than or equal to the endTime, and check against the dataInitialState times
    if (stateStartTime >= stateEndTime && isNotDefaultTimes) {

      this.props.openErrorDialog({
        title: 'Invalid time range',
        content: 'Start time must always be before the end time!',
        okCallback: this.props.closeAlertDialog
      });

      return;

    }

    const requestObject = this.buildRequestObject(this.state.data);

    const successCallback = () => {
      this.clearData();
      this.props.openSuccessDialog({
        title: 'Success',
        content: 'Your appointment wait list request was successfully submitted.',
        okCallback: this.props.closeAlertDialog
      });
    };

    const errorCallback = () =>
      this.props.openErrorDialog({
        title: 'Error',
        content: 'Unable to wait list for an appointment at this time. Please try again later.',
        okCallback: this.props.closeAlertDialog
      });

    this.props.request(requestObject, successCallback, errorCallback);

  };

  isValidDate = () => {
    return (this.state.data.firstAvailable || this.state.data.date);
  };

  /**
   * Checks if an "other" option has been included in a list of filter options. The value of an "other" option
   * will always have an ID = 0 and EXTERNAL_ID = null with a label of "other" or "OTHER"
   *
   * @param value - FilterItem.java
   * @returns {boolean}
   */
  checkForSelectionOfOther = value => {
    return !!(value && value.id === 0 && !value.externalId && value.label.match(/[oO][tT][hH][eE][rR]/));
  };

  // Other reason has a null external ID and the label has the word 'other' with some or no capital letters.
  isOtherReasonVisible = () => {
    let otherReasonVisible = false;

    this.state.data.filters.forEach(value => {
      if (this.checkForSelectionOfOther(value)) {
        otherReasonVisible = true;
      }
    });

    return otherReasonVisible;

  };

  isLoadingFilterLabels = loading => contains(WaitlistTypes.GET_DISPLAY_PROPERTIES, loading.requests);

  isLoadingRequestWaitlist = loading => contains(WaitlistTypes.REQUEST_WAITLIST, loading.requests);

  getVisibleFilterProperties = filters => filters.filter((filter) => filter.visible);

  renderReactSelectFilterComponent = () => {

    const visibleFilterProperties = this.getVisibleFilterProperties(this.props.waitlist.filters);

    let reactSelectFilterComponent = visibleFilterProperties.map((filterObj, index) => {

      return (
        <tr key={filterObj.label}>
          <td className={s.label}>{filterObj.label}</td>
          <td className={s.controlCell}>
            <Select
              className={cx(s.select, 'schedulingFilter')}
              clearable={false}
              disabled={!this.state.filterOptions[index] || this.state.filterOptions[index].length === 0}
              id={`waitlist__filter_${index}`}
              name={`filter_${index}`}
              onChange={value => this.handleFilterSelection(index, value)}
              options={this.state.filterOptions[index]}
              searchable={false}
              value={this.state.data.filters[index]}
            />
          </td>
        </tr>
      );

    });

    const isLoadingFilterLabels = this.isLoadingFilterLabels(this.props.loading);

    if (isLoadingFilterLabels) {
      return (
        <tr>
          <td className={s.label}>
            Loading Waitlist options...
          </td>
          <td className={s.controlCell}>
            <LoadingState />
          </td>
        </tr>
      );
    }
    else {
      return (
        reactSelectFilterComponent
      );
    }
  };

  renderReactTextAreaComponent = () => {
    const {label} = this.props.waitlist.optional;
    return (
      <tr>
        <td className={s.label}>
          {label}
        </td>
        <td className={s.controlCell}>
          <textarea
            className={s.textarea}
            id="waitlist__other"
            name="other"
            onChange={this.handleOtherChange}
            placeholder={label}
            value={this.state.data.other}
          />
        </td>
      </tr>
    );
  };

  isOptionalToggleable = () => this.props.waitlist.optional.toggleable;

  showOptionalComponent = () => {

    // Map wait list configuration API response optional object properties
    const {visible} = this.props.waitlist.optional;

    // Optional has been configured to never be shown
    if (!visible) {
      return false;
    }

    // Optional is not toggleable, therefore always show it
    const toggleable = this.isOptionalToggleable();
    if (!toggleable) {
      return true;
    }

    // Optional is toggleable AND a corresponding filter option of "OTHER" has been chosen
    return !!(toggleable && this.isOtherReasonVisible());
  };

  handleFilterOptional = () => {
    if (this.showOptionalComponent()) {
      return this.renderReactTextAreaComponent(this.props.waitlist.optional.label)
    }
    else {
      return null;
    }
  };

  render = () => {
    const isLoadingRequestWaitlist = this.isLoadingRequestWaitlist(this.props.loading);

    return (
      <div className={s.component}>
        <h1 className={s.header}>
          {this.header}
        </h1>
        <p className={s.instructions}>
          {this.instructions}
        </p>
        <table className={s.table}>
          <tbody>
            {this.renderReactSelectFilterComponent()}
            {this.handleFilterOptional()}
            <tr>
              <td className={s.label}>When</td>
              <td className={s.controlCell}>
                <Checkbox
                  className={s.firstAvailableCheckbox}
                  checked={this.state.data.firstAvailable}
                  id="waitlist__first_available"
                  onChange={this.handleFirstAvailableSelection}
                />
                <label
                  className={s.firstAvailableLabel}
                  htmlFor="waitlist__first_available"
                >
                  First Available
                </label>
              </td>
            </tr>
            <tr>
              <td/>
              <td className={s.controlCell}>
                <div className={s.backgroundLine}>
                  <span className={s.text}>OR</span>
                </div>
              </td>
            </tr>
            <tr>
              <td/>
              <td className={s.controlCell}>
                <DatePickerInput
                  disabled={ this.state.data.firstAvailable }
                  hasInputBorder
                  id="waitlist__date"
                  minDate={this.state.minDate}
                  onChange={this.handleDateChange}
                  value={this.state.data.date}
                />
              </td>
            </tr>
            <tr>
              <td/>
              <td className={s.controlCell}>
                <Select
                  className={cx(s.select, 'schedulingFilter')}
                  clearable={false}
                  disabled={this.state.data.firstAvailable}
                  id={`waitlist__when`}
                  name="when"
                  onChange={this.handleWhenSelection}
                  options={this.whenOptions}
                  searchable={false}
                  value={this.state.data.when}
                />
              </td>
            </tr>
            <tr>
              <td/>
              <td className={s.controlCell}>
                <TimeInput
                  disabled={this.state.data.firstAvailable || !this.state.data.when || this.state.data.when === 'before'}
                  id="waitlist__start_date"
                  onChange={this.handleStartTimeChange}
                  timeIncrement={30}
                  value={this.state.data.startTime}
                />
              </td>
            </tr>
            <tr>
              <td/>
              <td className={s.controlCell}>
                <TimeInput
                  disabled={this.state.data.firstAvailable || !this.state.data.when ||  this.state.data.when === 'after'}
                  id="waitlist__end_date"
                  onChange={this.handleEndTimeChange}
                  value={this.state.data.endTime}
                />
              </td>
            </tr>
            <tr>
              <td className={s.label}>Notes</td>
              <td className={s.controlCell}>
                <textarea
                  className={s.textarea}
                  id="waitlist__notes"
                  name="notes"
                  onChange={this.handleNotesChange}
                  placeholder="Add a message to your provider..."
                  value={this.state.data.notes}
                />
              </td>
            </tr>
            <tr>
            <td colSpan={2} className={cx(s.controlCell, s.btnCell)}>
              <button
                disabled={!this.isValidRequest() || isLoadingRequestWaitlist}
                id="waitlist__request_button"
                className={s.btn}
                onClick={this.handleSubmit}
              >
                {isLoadingRequestWaitlist ?
                  <LoadingState context="waitlist" /> :
                  'Request Waitlist'
                }
              </button>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  loading: state.loadingState,
  waitlist: state.waitlist,
});

const mapDispatchToProps = dispatch => ({
  filter: (options, successCb) => dispatch(filter(options, successCb)),
  getDisplayProperties: () => dispatch(getDisplayProperties()),
  request: (requestObject, successCb, errorCb) => dispatch(request(requestObject, successCb, errorCb)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Waitlist);



// WEBPACK FOOTER //
// ./src/components/Pages/Appointments/Waitlist/Waitlist.js